import {
    FormFields,
    FormFieldsPartial,
} from "../redux/modules/my-shop/form-fields-reducer";

import { DraperyItemDetailsFieldsPartial, bundleSelectionsInitialState } from "../redux/modules/my-shop/drapery-item-details-fields-reducer";
import { FabricsOrLiningsItemDetailsFieldsPartial } from "../redux/modules/my-shop/fabrics-or-linings-item-details-fields-reducer";
import { GetAllQueryParams } from "../interfaces/item";
import axiosInstance from "./axios-instance";

type AccountScopedPayload = {
    accountId?: number;
    id?: number | null;
};

type FormFieldsScopedPayload = FormFieldsPartial & AccountScopedPayload;
type ComboDraperyPayload = FormFieldsPartial &
    DraperyItemDetailsFieldsPartial &
    AccountScopedPayload;
type ComboFabricsOrLinings = FormFieldsPartial &
    FabricsOrLiningsItemDetailsFieldsPartial &
    AccountScopedPayload;

const getScopedAccountId = (accountId?: number): number => {
    if (typeof window === "undefined") {
        return Number(accountId || 0);
    }
    const ownerAccountId = Number(localStorage.getItem("ownerAccountId") || "0");
    if (ownerAccountId > 0) {
        return ownerAccountId;
    }
    if (Number(accountId) > 0) {
        return Number(accountId);
    }
    return Number(localStorage.getItem("accountId") || "0");
};

const getAll = async (queryParams?: GetAllQueryParams) => {
    try {
        const scopedAccountId = getScopedAccountId();
        const response = await axiosInstance.get<FormFields[]>("/item", {
            params: {
                ...queryParams,
                accountId: scopedAccountId,
            },
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching items:", error);
        throw error;
    }
};

const getRecord = async ({ recordId }: { recordId: number }) => {
    try {
        const scopedAccountId = getScopedAccountId();
        const response = await axiosInstance.get<FormFields>(
            `/item/${recordId}?accountId=${scopedAccountId}`
        );
        return response.data;
    } catch (error) {
        console.error("Error adding new item:", error);
        throw error;
    }
};

const addRecord = async (payload: FormFieldsScopedPayload) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response = await axiosInstance.post<FormFieldsPartial>(
            `/item?accountId=${scopedAccountId}`,
            payload
        );
        return response;
    } catch (error) {
        console.error("Error adding new item:", error);
        throw error;
    }
};

const updateRecord = async (payload: FormFieldsScopedPayload) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response = await axiosInstance.put<FormFieldsPartial>(
            `/item/${payload.id}?accountId=${scopedAccountId}`,
            payload
        );
        return response;
    } catch (error) {
        console.error("Error updating item:", error);
        throw error;
    }
};

const updateInactiveBulkRecord = async (payload: number[]) => {
    try {
        const scopedAccountId = getScopedAccountId();
        const response = await axiosInstance.put<FormFieldsPartial>(
            `/item?accountId=${scopedAccountId}`,
            payload
        );
        return response;
    } catch (error) {
        console.error("Error updating item:", error);
        throw error;
    }
};

const getDraperyRecord = async ({ recordId }: { recordId: number }) => {
    try {
        const scopedAccountId = getScopedAccountId();
        let response = await axiosInstance.get<ComboDraperyPayload>(
            `/item/drapery/${recordId}?accountId=${scopedAccountId}`,
        );
        
        if (
            response && response.data && 
            response.data.extraLargePanelsLengthInches && 
            response.data.extraLargePanelsLengthInches !== '' &&
            response.data.extraLargePanelsLengthInches !== null
        ) {
            response.data.extraLargePanelsLengthInches = parseFloat(response.data.extraLargePanelsLengthInches).toString();
        }

        if (
            response && response.data && 
            response.data.extraLargePanelsWidthInches && 
            response.data.extraLargePanelsWidthInches !== '' &&
            response.data.extraLargePanelsWidthInches !== null
        ) {
            response.data.extraLargePanelsWidthInches = parseFloat(response.data.extraLargePanelsWidthInches).toString();
        }

        if (response && response.data) {
            const selectionsFromApi: any = response.data.bundleSelections || {};
            const normalizedSelections: any = {
                ...bundleSelectionsInitialState,
                ...selectionsFromApi,
            };
            Object.keys(normalizedSelections).forEach((key) => {
                normalizedSelections[key] = Boolean(normalizedSelections[key]);
            });
            response.data.bundleSelections = normalizedSelections;
            response.data.isBundlingSelected = Boolean(response.data.isBundlingSelected);
        }

        return response.data;
    } catch (error) {
        console.error("Error fetching drapery item:", error);
        throw error;
    }
};

const addDraperyRecord = async (payload: ComboDraperyPayload) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response = await axiosInstance.post<ComboDraperyPayload>(
            `/item/drapery?accountId=${scopedAccountId}`,
            payload
        );
        return response;
    } catch (error) {
        console.error("Error adding new drapery item:", error);
        throw error;
    }
};

const updateDraperyRecord = async (payload: ComboDraperyPayload) => {
    try {
        if (payload.supplierId === 0) {
            payload.supplierId = null
        }
        const scopedAccountId = getScopedAccountId(payload.accountId);

        const response = await axiosInstance.put<ComboDraperyPayload>(
            `/item/drapery/${payload.id}?accountId=${scopedAccountId}`,
            payload
        );
        return response;
    } catch (error) {
        console.error("Error updating drapery item:", error);
        throw error;
    }
};

const deletePleat = async (itemId: number | null, newPleatId: any) => {
    try {
        const scopedAccountId = getScopedAccountId();
        const response = await axiosInstance.put<ComboDraperyPayload>(
            `/item/pleats/${newPleatId}/${itemId}?accountId=${scopedAccountId}`
        );        
        return response.data; 
    } catch (error) {
        console.error("Error deleting pleat:", error);
        throw error;
    }
};


const getFabricsOrLiningsRecord = async ({
    recordId,
    draperyOrderFabricId
}: {
    recordId: number;
    draperyOrderFabricId?: number;
}) => {
    try {
        const scopedAccountId = getScopedAccountId();
        const response = await axiosInstance.get<ComboFabricsOrLinings>(
           `/item/fabrics-or-linings/${recordId}?accountId=${scopedAccountId}${draperyOrderFabricId ? `&id=${draperyOrderFabricId}` : ""}`
        );

        return response.data;
    } catch (error) {
        console.error("Error fetching fabrics or linings item:", error);
        throw error;
    }
};

const addFabricsOrLiningsRecord = async (payload: ComboFabricsOrLinings) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response = await axiosInstance.post<ComboFabricsOrLinings>(
            `/item/fabrics-or-linings?accountId=${scopedAccountId}`,
            payload
        );
        return response;
    } catch (error) {
        console.error("Error adding new fabrics or linings item:", error);
        throw error;
    }
};

const updateFabricsOrLiningsRecord = async (payload: ComboFabricsOrLinings) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response = await axiosInstance.put<ComboFabricsOrLinings>(
            `/item/fabrics-or-linings/${payload.id}?accountId=${scopedAccountId}`,
            payload
        );
        return response;
    } catch (error) {
        console.error("Error updating fabrics or linings item:", error);
        throw error;
    }
};

const seedItemsOnSignup = async () => {
        try {
            const scopedAccountId = getScopedAccountId();
            const response = await axiosInstance.post(`/item/seed-on-signup?accountId=${scopedAccountId}`);
            return response.data;
    } catch (error) {
        console.error("Error seeding items on signup:", error);
        throw error;
    }
};

const seedItemsOnLogin = async () => {
    try {
        const scopedAccountId = getScopedAccountId();
        const response = await axiosInstance.post(`/item/seed-on-login?accountId=${scopedAccountId}`);
        return response.data;
    } catch (error) {
        console.error("Error seeding items on login:", error);
        throw error;
    }
};

const getAllOtherItems = async (accountId: number, term: string) => {
    try {
        const scopedAccountId = getScopedAccountId(accountId);
        const response = await axiosInstance.get(`/item/other-items/get-all?accountId=${scopedAccountId}`);
        return response.data;
    } catch (error) {
        console.error("Error get all other items:", error);
        throw error;
    } 
}


export {
    getAll,
    getRecord,
    addRecord,
    updateRecord,
    updateInactiveBulkRecord,
    getDraperyRecord,
    addDraperyRecord,
    updateDraperyRecord,
    deletePleat,
    getFabricsOrLiningsRecord,
    addFabricsOrLiningsRecord,
    updateFabricsOrLiningsRecord,
    seedItemsOnSignup,
    seedItemsOnLogin,
    getAllOtherItems
};
